import { ReserveNewPreReserveUrl } from './urls'
import { NavigateFunction } from 'react-router-dom'
import showResponseError from '../../../../utils/showResponseError'
/* eslint-disable camelcase */
import {
  createSlice,
  PayloadAction,
  createAsyncThunk,
  ActionReducerMapBuilder,
} from '@reduxjs/toolkit'

import { AppHttpConfig as API } from '../../../../services/httpConfig'

import toastify from '../../../../utils/toastify'
import { ToastType } from '../../../../types/generalType'
import { t } from 'i18next'
import {
  IReserveNewPreReserveInitialState,
  TCreatePreReserve,
  TPilgrim,
  TPreviousPilgrims,
} from './types'

const initialState: IReserveNewPreReserveInitialState = {
  preReservationStatus: {
    loading: false,
    isActive: false,
  },
  perviousPilgrims: {
    loading: false,
    city: '',
    province: '',
    zone: '',
    leaderInfo: null,
    pilgrims: [],
  },
  newReserve: {
    province: '',
    provinceName: '',
    city: '',
    zone: '',
    transportation: 0,
    phoneNumber: '',
    previousPilgrimList: [],
    newReservePilgrimList: [],
  },
  createPreReserve: {
    loading: false,
  },
}

// Get PreReservation Status
export const getPreReservationStatus = createAsyncThunk<boolean>(
  'reserveNewPreReserve/getPreReservationStatus',
  async () => {
    try {
      const response = await API.get(ReserveNewPreReserveUrl.GetPreReservationStatus)
        .then((res) => {
          if (res.status === 200) {
            return res.data
          }
          if (res.status === 204) {
            return res.data
          }
        })
        .catch((error) => {
          showResponseError(error)
        })
      return response
    } catch (error) {
      showResponseError(error)
    }
  },
)
// Get Previous Pilgrims By NationalCode
export const getPreviousPilgrims = createAsyncThunk<TPreviousPilgrims, string>(
  'reserveNewPreReserve/getPreviousPilgrims',
  async (nationalCode, thunkAPI) => {
    try {
      const response = await API.get(
        `${ReserveNewPreReserveUrl.GetPreviousPilgrims}?NationalCode=${nationalCode}`,
      )
        .then((res) => {
          if (res.status === 200) {
            return res.data
          }
          if (res.status === 204) {
            return res.data
          }
        })
        .catch((error) => {
          showResponseError(error)
          return thunkAPI.rejectWithValue(error[0])
        })
      return response
    } catch (error) {
      showResponseError(error)
    }
  },
)
// Create PreReserve
export const createPreReserve = createAsyncThunk<
  number,
  { data: TCreatePreReserve; navigate: NavigateFunction }
>('reserveNewPreReserve/createPreReserve', async (command, thunkAPI) => {
  const { data, navigate } = command
  try {
    const response = await API.post(ReserveNewPreReserveUrl.CreatePreReserve, data)
      .then((res) => {
        if (res.status === 201) {
          toastify(ToastType.SUCCESS, { operatin: t('save'), pageName: t('newPreReserve') })
          thunkAPI.dispatch(clearStates())
          navigate('/')
          return res.data
        }
        if (res.status === 204) {
          return res.data
        }
      })
      .catch((error) => {
        showResponseError(error)
        return thunkAPI.rejectWithValue(error[0])
      })
    return response
  } catch (error) {
    showResponseError(error)
  }
})

const reserveNewPreReserveSlice = createSlice({
  name: 'reserveNewPreReserve',
  initialState,
  reducers: {
    clearStates: (state) => {
      state.preReservationStatus.loading = false
      state.preReservationStatus.isActive = false
      state.perviousPilgrims.loading = false
      state.perviousPilgrims.city = ''
      state.perviousPilgrims.province = ''
      state.perviousPilgrims.zone = ''
      state.newReserve.city = ''
      state.newReserve.province = ''
      state.newReserve.provinceName = ''
      state.newReserve.zone = ''
      state.perviousPilgrims.leaderInfo = null
      state.perviousPilgrims.pilgrims = []
      state.newReserve.newReservePilgrimList = []
      state.newReserve.previousPilgrimList = []
      state.preReservationStatus.isActive = false
    },
    setPreviousPilgrimList: (state, action: PayloadAction<TPilgrim[]>) => {
      state.newReserve.previousPilgrimList = action.payload
    },
    setNewReservePilgrimList: (state, action: PayloadAction<TPilgrim[]>) => {
      state.newReserve.newReservePilgrimList = action.payload
    },
    setCity: (state, action: PayloadAction<string>) => {
      state.newReserve.city = action.payload
    },
    setProvince: (state, action: PayloadAction<string>) => {
      state.newReserve.province = action.payload
    },
    setProvinceName: (state, action: PayloadAction<string>) => {
      state.newReserve.provinceName = action.payload
    },
    setZone: (state, action: PayloadAction<string>) => {
      state.newReserve.zone = action.payload
    },
    setTransportation: (state, action: PayloadAction<number | string>) => {
      state.newReserve.transportation = action.payload
    },
    setPhoneNumber: (state, action: PayloadAction<string>) => {
      state.newReserve.phoneNumber = action.payload
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<IReserveNewPreReserveInitialState>) => {
    // Get PreReservation Status
    builder
      .addCase(getPreReservationStatus.pending, (state) => {
        state.preReservationStatus.loading = true
      })
      .addCase(getPreReservationStatus.fulfilled, (state, action: PayloadAction<boolean>) => {
        state.preReservationStatus.loading = false
        state.preReservationStatus.isActive = action.payload
      })
      .addCase(getPreReservationStatus.rejected, (state) => {
        state.preReservationStatus.loading = false
      })
    // Get Previous Pilgrims By NationalCode
    builder
      .addCase(getPreviousPilgrims.pending, (state) => {
        state.perviousPilgrims.loading = true
      })
      .addCase(getPreviousPilgrims.fulfilled, (state, action: PayloadAction<TPreviousPilgrims>) => {
        state.perviousPilgrims.loading = false

        const leader = action.payload.pilgrims.filter((pilgrim) => pilgrim.isLeader)
        const pilgrimList = action.payload.pilgrims.filter((pilgrim) => !pilgrim.isLeader)

        state.perviousPilgrims.province = action.payload.province
        state.perviousPilgrims.city = action.payload.city
        state.perviousPilgrims.zone = action.payload.zone

        state.newReserve.province = ''
        state.newReserve.provinceName = action.payload.province
        state.newReserve.city = action.payload.city
        state.newReserve.zone = action.payload.zone

        state.perviousPilgrims.leaderInfo = leader[0]
        state.perviousPilgrims.pilgrims = pilgrimList

        state.newReserve.previousPilgrimList = pilgrimList
        state.newReserve.newReservePilgrimList = leader
      })
      .addCase(getPreviousPilgrims.rejected, (state) => {
        state.perviousPilgrims.loading = false
      })
    // Create PreReserve
    builder
      .addCase(createPreReserve.pending, (state) => {
        state.createPreReserve.loading = true
      })
      .addCase(createPreReserve.fulfilled, (state) => {
        state.createPreReserve.loading = false
      })
      .addCase(createPreReserve.rejected, (state) => {
        state.createPreReserve.loading = false
      })
  },
})

export const {
  clearStates,
  setPreviousPilgrimList,
  setNewReservePilgrimList,
  setCity,
  setProvince,
  setProvinceName,
  setZone,
  setTransportation,
  setPhoneNumber,
} = reserveNewPreReserveSlice.actions

export default reserveNewPreReserveSlice.reducer
